"use client";

import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  customElementAttributeMappers,
  setCustomElementMetadata,
} from '@/lib/customElements';
import { Button } from '@/components/base/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/base/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/base/popover';

interface Option {
  value: string;
  label: string;
}

interface ComboboxProps {
  options: Option[];
  width?: string;
  name?: string;
  placeholder?: string;
  emptyMessage?: string;
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  style?: React.CSSProperties;
}

export function Combobox({
  options,
  width = '200px',
  placeholder = 'Select option...',
  emptyMessage = 'No option found.',
  defaultValue = '',
  value: controlledValue,
  onChange,
  name = undefined,
  style = {},
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const [searchTerm, setSearchTerm] = React.useState('');
  
  // Use controlled value if provided, otherwise use internal state
  const value = controlledValue !== undefined ? controlledValue : internalValue;
  
  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return options;
    return options.filter(option => 
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  // Clear search when popover closes
  React.useEffect(() => {
    if (!open) {
      setSearchTerm('');
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          name={name}
          aria-expanded={open}
          className={cn('justify-between', `w-[${width}]`)}
          style={style}
        >
          {value
            ? options.find((option) => option.value === value)?.label
            : placeholder}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('p-0', `w-[${width}]`)}>
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Search..." 
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={(currentValue) => {
                    const newValue = currentValue === value ? '' : currentValue;
                    if (onChange) {
                      onChange(newValue);
                    } else {
                      setInternalValue(newValue);
                    }
                    setOpen(false);
                  }}
                >
                  {option.label}
                  <Check
                    className={cn(
                      'ml-auto',
                      value === option.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const ComboboxWebComponentWrapper: React.FC<
  ComboboxProps & {
    _domElement?: HTMLElement;
  }
> = (props) => {
  return <Combobox {...props} />;
};

setCustomElementMetadata({
  component: ComboboxWebComponentWrapper,
  tagName: 'raes-combobox',
  observedAttributes: {
    name: customElementAttributeMappers.string,
    width: customElementAttributeMappers.string,
    placeholder: customElementAttributeMappers.string,
    'empty-message': customElementAttributeMappers.string,
    'default-value': customElementAttributeMappers.string,
    options: customElementAttributeMappers.json
  },
});

export { ComboboxWebComponentWrapper };
